import { useEffect, useState } from "react"; // v3
import { ArrowUpRight, Bookmark, Chrome, KeyRound, LogIn, LogOut, Sparkles, User } from "lucide-react";
import { toast } from "sonner";
import AuthModal from "@/components/AuthModal";
import EmptyState from "@/components/EmptyState";
import FilterNotice from "@/components/FilterNotice";
import IframeBanner from "@/components/IframeBanner";
import LotGrid from "@/components/LotGrid";
import ModeTabs from "@/components/ModeTabs";
import ResultsHeader from "@/components/ResultsHeader";
import SavedSearches from "@/components/SavedSearches";
import SearchForm from "@/components/SearchForm";
import SettingsDialog from "@/components/SettingsDialog";
import WatchlistView from "@/components/WatchlistView";
import WelcomeState from "@/components/WelcomeState";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { saveSearch } from "@/lib/api";
import { getCookieStatus } from "@/lib/settings";
import { useExtensionStatus } from "@/lib/extension";
import useAuth from "@/hooks/useAuth";
import useSearch from "@/hooks/useSearch";
import useWatchlist from "@/hooks/useWatchlist";

function filterActive(items) {
  return items.filter((i) => i.seconds_remaining === null || i.seconds_remaining > 0);
}

function CountryFilter({ items, value, onChange }) {
  const countries = [...new Set(items.map((i) => i.seller_country).filter(Boolean))].sort();
  if (countries.length === 0) return null;
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-black/40 border border-white/15 text-white/80 text-xs uppercase tracking-[0.18em] px-3 py-2 focus:outline-none focus:border-gold hover:border-white/30 transition-colors"
    >
      <option value="">Tutti i paesi</option>
      {countries.map((c) => (
        <option key={c} value={c}>{c}</option>
      ))}
    </select>
  );
}

const HERO_BG =
  "https://images.unsplash.com/photo-1633511297406-bc9815401b9b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHxvaWwlMjBwYWludGluZyUyMGNhbnZhcyUyMHRleHR1cmV8ZW58MHx8fHwxNzgwNjg0ODY4fDA&ixlib=rb-4.1.0&q=85";
const EMPTY_ITEMS = [];

function HeroHeader({ extensionActive, cookieActive, onOpenSettings, user, onLogin, onLogout }) {
  const active = extensionActive || cookieActive;
  const Icon = extensionActive ? Chrome : KeyRound;
  const label = extensionActive
    ? "Estensione attiva"
    : cookieActive
      ? "Cookie attivo"
      : "Sessione Catawiki";
  return (
    <header className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pt-8 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 border border-gold/60 flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-gold" />
        </div>
        <span className="font-display text-xl tracking-wide">Cercatore Catawiki</span>
      </div>
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={onOpenSettings}
          className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] transition-colors ${
            active ? "text-emerald-400 hover:text-emerald-300" : "text-white/60 hover:text-gold"
          }`}
          data-testid="settings-open"
        >
          <Icon className="w-3.5 h-3.5" />
          {label}
        </button>
        <a
          href="https://www.catawiki.com"
          target="_blank"
          rel="noopener noreferrer"
          className="hidden sm:inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-white/60 hover:text-gold transition-colors"
          data-testid="catawiki-link"
        >
          Catawiki.com <ArrowUpRight className="w-3.5 h-3.5" />
        </a>
        {user ? (
          <div className="flex items-center gap-3">
            <span className="hidden sm:flex items-center gap-1.5 text-xs text-white/50">
              <User className="w-3 h-3" />
              {user.email}
            </span>
            <button
              type="button"
              onClick={onLogout}
              className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-white/40 hover:text-red-400 transition-colors"
              data-testid="logout-button"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Esci</span>
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={onLogin}
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-white/60 hover:text-gold transition-colors"
            data-testid="login-button"
          >
            <LogIn className="w-3.5 h-3.5" />
            Accedi
          </button>
        )}
      </div>
    </header>
  );
}

function HeroCopy() {
  return (
    <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pt-20 pb-12">
      <p className="text-xs uppercase tracking-[0.3em] text-gold/80 mb-6" data-testid="hero-eyebrow">
        Aste · Collezionismo · Pezzi unici
      </p>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-4xl">
        Esplora <em className="text-gold not-italic">l'asta</em> di Catawiki.
        <br />
        <span className="text-white/55">Cerca con precisione tra migliaia di lotti.</span>
      </h1>
      <p className="mt-6 text-white/55 max-w-xl text-base sm:text-lg font-light">
        Indica parole chiave, categoria, budget e tempo rimanente. Visualizza i lotti che ti interessano davvero.
      </p>
    </div>
  );
}

function LoadMoreButton({ visible, loading, onClick }) {
  if (!visible) return null;
  return (
    <div className="mt-12 flex justify-center">
      <button
        type="button"
        onClick={onClick}
        disabled={loading}
        className="rounded-none border border-white/15 hover:border-gold hover:text-gold text-white px-12 py-5 text-xs uppercase tracking-[0.25em] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="load-more-button"
      >
        {loading ? "Caricamento..." : "Carica altri 48 lotti"}
      </button>
    </div>
  );
}

function ResultsSection({ searching, results, hasSearched, mode, lastQueryLabel, sectionRef, loadingMore, onLoadMore, user, onSave, lastPayload }) {
  const hiddenCount = results ? results.returned - results.filtered_count : 0;
  const hasResults = !!results && results.filtered_count > 0;
  const noResults = !!results && hasSearched && results.filtered_count === 0;
  const canSave = user && hasSearched && !searching && lastPayload;
  const pickupMode = false;

  return (
    <section
      ref={sectionRef}
      className="relative max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24 scroll-mt-6"
      data-testid="results-section"
    >
      {hasSearched && (
        <div className="flex items-start justify-between gap-4 mb-0">
          <ResultsHeader searching={searching} results={results} label={lastQueryLabel} />
          {canSave && (
            <button
              type="button"
              onClick={() => onSave(lastPayload)}
              className="shrink-0 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-white/40 hover:text-gold transition-colors mt-1"
              data-testid="save-search-results-button"
            >
              <Bookmark className="w-3.5 h-3.5" />
              Salva ricerca
            </button>
          )}
        </div>
      )}
      {searching && <LotGrid loading items={EMPTY_ITEMS} />}
      {!searching && results && results.returned > 0 && (
        <FilterNotice hiddenCount={hiddenCount} />
      )}
      {!searching && noResults && <EmptyState />}
      {!searching && hasResults && <LotGrid items={results.items} pickupMode={pickupMode} />}
      {!searching && hasResults && (
        <LoadMoreButton
          visible={!!results?.has_more}
          loading={loadingMore}
          onClick={onLoadMore}
        />
      )}
      {!hasSearched && <WelcomeState mode={mode} />}
    </section>
  );
}

function SaveSearchDialog({ open, onOpenChange, onConfirm }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await onConfirm(name.trim());
      setName("");
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setName(""); onOpenChange(v); }}>
      <DialogContent className="bg-surface border-white/10 text-foreground max-w-sm" data-testid="save-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Bookmark className="w-4 h-4 text-gold" />
            Salva ricerca
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">Nome ricerca</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleConfirm()}
              placeholder="es. Rolex vintage, Poster anni 70…"
              className="input-luxury w-full text-base py-2"
              autoFocus
              data-testid="save-name-input"
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="text-white/50 text-xs"
            >
              Annulla
            </Button>
            <Button
              type="button"
              disabled={!name.trim() || busy}
              onClick={handleConfirm}
              className="rounded-none bg-white text-black hover:bg-gold hover:text-black text-xs uppercase tracking-[0.2em] px-6 disabled:opacity-50"
              data-testid="save-confirm-button"
            >
              Salva
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function SearchPage() {
  const {
    mode, switchMode,
    categories, loadingCategories,
    results, searching, loadingMore, hasSearched, lastQueryLabel, lastPayload, resultsRef,
    runStandardSearch, runMultidaySearch,
    runPier4Search, runPier5Search, runPier6Search, runPier7Search, runPier8Search, runPickupLiveSearch, runCoglioneSearch, runCoglione2Search, runWineSearch, loadMore, loadMoreCoglione2,
  } = useSearch();

  const { user, login, register, logout } = useAuth();
  const { count: watchlistCount } = useWatchlist();
  const [authOpen, setAuthOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [pendingSaveParams, setPendingSaveParams] = useState(null);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [cookieStatus, setCookieStatus] = useState({ has_cookie: false });
  const extension = useExtensionStatus();

  useEffect(() => {
    getCookieStatus().then(setCookieStatus).catch(() => {});
  }, []);

  const handleSaveSearch = (params) => {
    if (!user) { setAuthOpen(true); return; }
    setPendingSaveParams(params);
    setSaveDialogOpen(true);
  };

  const handleConfirmSave = async (name) => {
    if (!pendingSaveParams) return;
    const params = {
      q: pendingSaveParams.q || null,
      category_id: pendingSaveParams.categoryId || null,
      min_price: pendingSaveParams.minPrice || null,
      max_price: pendingSaveParams.maxPrice || null,
      max_hours_remaining: pendingSaveParams.maxHoursRemaining || null,
      seller_countries: pendingSaveParams.sellerCountries || null,
      local_only: pendingSaveParams.localOnly || false,
    };
    try {
      await saveSearch({ name, params });
      toast.success("Ricerca salvata.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvare la ricerca.");
      throw e;
    } finally {
      setPendingSaveParams(null);
    }
  };

  const isSavedMode = mode === "saved";
  const isWatchlistMode = mode === "watchlist";
  const [pier4Country, setPier4Country] = useState("");
  const [pier5Country, setPier5Country] = useState("");
  const [pier6Country, setPier6Country] = useState("");
  const [pier7Country, setPier7Country] = useState("");
  const [pier8Country, setPier8Country] = useState("");
  const [coglioneQ, setCoglioneQ] = useState("");
  const [wineCategory, setWineCategory] = useState("");
  const [wineCountry, setWineCountry] = useState("");

  const isPier4Mode = mode === "pier4";
  const isPier5Mode = mode === "pier5";
  const isPier6Mode = mode === "pier6";
  const isPier7Mode = mode === "pier7";
  const isPier8Mode = mode === "pier8";
  const isPickupLiveMode = mode === "pickup_live";
  const isCoglioneMode = mode === "coglione";
  const isCoglione2Mode = mode === "coglione2";
  const isWineMode = mode === "wine";
  const isSearchMode = !isSavedMode && !isPier4Mode && !isPier5Mode && !isPier6Mode && !isPier7Mode && !isPier8Mode && !isPickupLiveMode && !isCoglioneMode && !isWatchlistMode && !isWineMode;

  useEffect(() => {
    if (isPier4Mode) runPier4Search();
  }, [isPier4Mode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isPier5Mode) runPier5Search();
  }, [isPier5Mode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isPier6Mode) {
      setPier6Country("");
      runPier6Search({});
    }
  }, [isPier6Mode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isPier7Mode) {
      setPier7Country("");
      runPier7Search({});
    }
  }, [isPier7Mode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isPier8Mode) {
      setPier8Country("");
      runPier8Search({});
    }
  }, [isPier8Mode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isCoglioneMode) {
      setCoglioneQ("");
      runCoglioneSearch({});
    }
  }, [isCoglioneMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const LIVE_REFRESH_S = 600; // 10 minuti
  const [liveRefreshIn, setLiveRefreshIn] = useState(LIVE_REFRESH_S);
  useEffect(() => {
    if (!isPickupLiveMode) return;
    runPickupLiveSearch();
    setLiveRefreshIn(LIVE_REFRESH_S);
    const interval = setInterval(() => {
      runPickupLiveSearch();
      setLiveRefreshIn(LIVE_REFRESH_S);
    }, LIVE_REFRESH_S * 1000);
    const tick = setInterval(() => setLiveRefreshIn((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => { clearInterval(interval); clearInterval(tick); };
  }, [isPickupLiveMode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isWineMode) {
      setWineCategory("");
      setWineCountry("");
      runWineSearch({});
    }
  }, [isWineMode]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="relative min-h-screen bg-background text-foreground" data-testid="search-page">
      <IframeBanner />

      <section className="relative overflow-hidden grain" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-[0.12]" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/70 to-background" />
        </div>

        <HeroHeader
          extensionActive={extension.installed}
          cookieActive={cookieStatus.has_cookie}
          onOpenSettings={() => setSettingsOpen(true)}
          user={user}
          onLogin={() => setAuthOpen(true)}
          onLogout={logout}
        />
        <HeroCopy />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pb-16">
          <ModeTabs mode={mode} onChange={switchMode} user={user} watchlistCount={watchlistCount} />

          {mode === "standard" && (
            <SearchForm
              categories={categories}
              loadingCategories={loadingCategories}
              onSubmit={runStandardSearch}
              submitting={searching}
              user={user}
              onSave={handleSaveSearch}
            />
          )}
          {mode === "multiday" && (
            <SearchForm
              mode="multiday"
              categories={categories}
              loadingCategories={loadingCategories}
              onSubmit={runMultidaySearch}
              submitting={searching}
              user={user}
              onSave={handleSaveSearch}
            />
          )}
          {mode === "coglione2" && (
            <SearchForm
              categories={categories}
              loadingCategories={loadingCategories}
              onSubmit={runCoglione2Search}
              submitting={searching}
              user={user}
              onSave={handleSaveSearch}
            />
          )}
        </div>
      </section>

      {isPier4Mode && (
        <section className="relative max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
          {results?.cache_stats && (
            <div className="mb-8 flex flex-col gap-4">
              <div className="flex items-baseline gap-4">
                <span className="text-2xl font-semibold text-gold">{results.total}</span>
                <span className="text-white/60 text-sm">lotti pickup-only in cache</span>
              </div>
              <p className="text-white/40 text-xs">
                Totale in DB: {results.cache_stats.total_in_db}
                {results.cache_stats.last_scraped_at && (
                  <> · Ultimo refresh: {new Date(results.cache_stats.last_scraped_at).toLocaleString("it-IT")}</>
                )}
              </p>
              <CountryFilter items={results.items} value={pier4Country} onChange={setPier4Country} />
            </div>
          )}
          {searching && <p className="text-white/40 text-sm">Caricamento…</p>}
          {!searching && results && results.items.length > 0 && (() => {
            const active = filterActive(results.items);
            const visible = pier4Country ? active.filter((i) => i.seller_country === pier4Country) : active;
            return visible.length > 0 ? <LotGrid items={visible} /> : <p className="text-white/40 text-sm">Nessun lotto attivo.</p>;
          })()}
          {!searching && hasSearched && results && results.items.length === 0 && (
            <p className="text-white/40 text-sm">Nessun lotto pickup-only nella cache. Lancia il job di scraping per popolarla.</p>
          )}
        </section>
      )}

      {isPier5Mode && (
        <section className="relative max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
          {results?.cache_stats && (
            <div className="mb-8 flex flex-col gap-4">
              <div className="flex items-baseline gap-4">
                <span className="text-2xl font-semibold text-gold">{results.total}</span>
                <span className="text-white/60 text-sm">lotti con pickup in cache</span>
              </div>
              <p className="text-white/40 text-xs">
                Totale in DB: {results.cache_stats.total_in_db}
                {results.cache_stats.last_scraped_at && (
                  <> · Ultimo refresh: {new Date(results.cache_stats.last_scraped_at).toLocaleString("it-IT")}</>
                )}
              </p>
              <CountryFilter items={results.items} value={pier5Country} onChange={setPier5Country} />
            </div>
          )}
          {searching && <p className="text-white/40 text-sm">Caricamento…</p>}
          {!searching && results && results.items.length > 0 && (() => {
            const active = filterActive(results.items);
            const visible = pier5Country ? active.filter((i) => i.seller_country === pier5Country) : active;
            return visible.length > 0 ? <LotGrid items={visible} pickupMode /> : <p className="text-white/40 text-sm">Nessun lotto attivo.</p>;
          })()}
          {!searching && hasSearched && results && results.items.length === 0 && (
            <p className="text-white/40 text-sm">Nessun lotto con pickup nella cache. Lancia il job di scraping per popolarla.</p>
          )}
        </section>
      )}

      {isPier6Mode && (
        <section className="relative max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
          {results?.pier6Mode && results.cache_stats && (
            <div className="mb-8 flex flex-col gap-4">
              <div className="flex items-baseline gap-4">
                <span className="text-2xl font-semibold text-gold">{results.total}</span>
                <span className="text-white/60 text-sm">lotti pickup-only (tutte le categorie)</span>
              </div>
              <p className="text-white/40 text-xs">
                Totale in DB: {results.cache_stats.total_in_db}
                {results.cache_stats.last_scraped_at && (
                  <> · Ultimo refresh: {new Date(results.cache_stats.last_scraped_at).toLocaleString("it-IT")}</>
                )}
              </p>
              {results.countries?.length > 0 && (
                <select
                  value={pier6Country}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPier6Country(v);
                    runPier6Search({ country: v || null });
                  }}
                  className="bg-black/40 border border-white/15 text-white/80 text-xs uppercase tracking-[0.18em] px-3 py-2 focus:outline-none focus:border-gold hover:border-white/30 transition-colors w-fit"
                >
                  <option value="">Tutti i paesi</option>
                  {results.countries.map((c) => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
              )}
            </div>
          )}
          {searching && <p className="text-white/40 text-sm">Caricamento…</p>}
          {!searching && results?.pier6Mode && results.items.length > 0 && (() => {
            const active = filterActive(results.items);
            return active.length > 0
              ? <LotGrid items={active} pickupMode />
              : <p className="text-white/40 text-sm">Nessun lotto attivo.</p>;
          })()}
          {!searching && hasSearched && results?.pier6Mode && results.items.length === 0 && (
            <p className="text-white/40 text-sm">Nessun lotto pickup-only trovato. Il job all-lots gira ogni notte alle 03:00 UTC.</p>
          )}
        </section>
      )}

      {isPier7Mode && (
        <section className="relative max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
          {results?.pier7Mode && results.cache_stats && (
            <div className="mb-8 flex flex-col gap-4">
              <div className="flex items-baseline gap-4">
                <span className="text-2xl font-semibold text-gold">{results.total}</span>
                <span className="text-white/60 text-sm">lotti pickup-only (cache aste)</span>
              </div>
              <p className="text-white/40 text-xs">
                Totale in DB: {results.cache_stats.total_in_db}
                {results.cache_stats.last_scraped_at && (
                  <> · Ultimo refresh: {new Date(results.cache_stats.last_scraped_at).toLocaleString("it-IT")}</>
                )}
              </p>
              {results.countries?.length > 0 && (
                <select
                  value={pier7Country}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPier7Country(v);
                    runPier7Search({ country: v || null });
                  }}
                  className="bg-black/40 border border-white/15 text-white/80 text-xs uppercase tracking-[0.18em] px-3 py-2 focus:outline-none focus:border-gold hover:border-white/30 transition-colors w-fit"
                >
                  <option value="">Tutti i paesi</option>
                  {results.countries.map((c) => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
              )}
            </div>
          )}
          {searching && <p className="text-white/40 text-sm">Caricamento…</p>}
          {!searching && results?.pier7Mode && results.items.length > 0 && (() => {
            const active = filterActive(results.items);
            return active.length > 0
              ? <LotGrid items={active} pickupMode />
              : <p className="text-white/40 text-sm">Nessun lotto attivo.</p>;
          })()}
          {!searching && hasSearched && results?.pier7Mode && results.items.length === 0 && (
            <p className="text-white/40 text-sm">Nessun lotto pickup-only trovato. Lancia il job auction-pickup-refresh per popolare la cache.</p>
          )}
        </section>
      )}

      {isPier8Mode && (
        <section className="relative max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
          {results?.pier8Mode && results.cache_stats && (
            <div className="mb-8 flex flex-col gap-4">
              <div className="flex items-baseline gap-4">
                <span className="text-2xl font-semibold text-gold">{results.total}</span>
                <span className="text-white/60 text-sm">lotti pickup-only (cache + live)</span>
              </div>
              <p className="text-white/40 text-xs">
                Totale DB: {results.cache_stats.total_in_db}
                {results.cache_stats.last_keyword_refresh && (
                  <> · Keyword refresh: {new Date(results.cache_stats.last_keyword_refresh).toLocaleString("it-IT")}</>
                )}
                {results.cache_stats.refresh_status === "running" && (
                  <span className="ml-2 text-gold">⟳ aggiornamento in corso…</span>
                )}
                {results.cache_stats.lots_saved_last_run > 0 && (
                  <> · +{results.cache_stats.lots_saved_last_run} nuovi lotti da keywords</>
                )}
              </p>
              {results.countries?.length > 0 && (
                <select
                  value={pier8Country}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPier8Country(v);
                    runPier8Search({ country: v || null });
                  }}
                  className="bg-black/40 border border-white/15 text-white/80 text-xs uppercase tracking-[0.18em] px-3 py-2 focus:outline-none focus:border-gold hover:border-white/30 transition-colors w-fit"
                >
                  <option value="">Tutti i paesi</option>
                  {results.countries.map((c) => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
              )}
            </div>
          )}
          {searching && <p className="text-white/40 text-sm">Caricamento… (prima chiamata può richiedere ~30s per la ricerca live)</p>}
          {!searching && results?.pier8Mode && results.items.length > 0 && (() => {
            const active = filterActive(results.items);
            return active.length > 0
              ? <LotGrid items={active} pickupMode />
              : <p className="text-white/40 text-sm">Nessun lotto attivo.</p>;
          })()}
          {!searching && hasSearched && results?.pier8Mode && results.items.length === 0 && (
            <p className="text-white/40 text-sm">Nessun lotto pickup-only trovato.</p>
          )}
        </section>
      )}

      {isCoglioneMode && (
        <section className="relative max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
          <div className="mb-8 flex flex-col gap-4">
            <div className="flex items-baseline gap-4">
              <span className="text-2xl font-semibold text-gold">
                {results?.coglioneMode ? results.total : "—"}
              </span>
              <span className="text-white/60 text-sm">lotti pickup_only · scadenza entro 1 ora</span>
            </div>
            <form
              onSubmit={(e) => { e.preventDefault(); runCoglioneSearch({ q: coglioneQ || null }); }}
              className="flex gap-2"
            >
              <input
                type="text"
                value={coglioneQ}
                onChange={(e) => setCoglioneQ(e.target.value)}
                placeholder="Keyword opzionale…"
                className="input-luxury text-sm py-2 px-3 w-64"
              />
              <button
                type="submit"
                disabled={searching}
                className="border border-white/15 hover:border-gold hover:text-gold text-white px-6 py-2 text-xs uppercase tracking-[0.2em] transition-colors disabled:opacity-50"
              >
                {searching ? "…" : "Cerca"}
              </button>
            </form>
          </div>
          {searching && <LotGrid loading items={[]} />}
          {!searching && results?.coglioneMode && results.items.length > 0 && (
            <LotGrid items={results.items} pickupMode />
          )}
          {!searching && results?.coglioneMode && results.items.length === 0 && (
            <p className="text-white/40 text-sm">Nessun lotto pickup_only in scadenza nell'ora successiva.</p>
          )}
        </section>
      )}

      {isPickupLiveMode && (
        <section className="relative max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
          <div className="mb-8 flex flex-col gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-2xl font-semibold text-red-400">
                {results?.pickupLiveMode ? results.total : "—"}
              </span>
              <span className="text-white/60 text-sm">lotti pickup con scadenza entro 30 minuti</span>
              <span className="ml-auto text-xs text-white/30 tabular-nums">
                {searching ? "Aggiornamento…" : `Prossimo aggiornamento: ${liveRefreshIn}s`}
              </span>
            </div>
            <p className="text-white/30 text-xs">
              Aste live Catawiki — {results?.totalScanned ? `${results.totalScanned} lotti scansionati · ` : ""}filtro: pickup_city · aggiornamento ogni 10 minuti
            </p>
          </div>
          {searching && <LotGrid loading items={[]} />}
          {!searching && results?.pickupLiveMode && results.items.length > 0 && (
            <LotGrid items={results.items} pickupMode />
          )}
          {!searching && results?.pickupLiveMode && results.items.length === 0 && (
            <p className="text-white/40 text-sm">Nessun lotto pickup in scadenza nei prossimi 30 minuti.</p>
          )}
        </section>
      )}

      {isWineMode && (
        <section className="relative max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
          <div className="mb-8 flex flex-col gap-4">
            <div className="flex items-baseline gap-4">
              <span className="text-2xl font-semibold text-gold">{results?.total ?? 0}</span>
              <span className="text-white/60 text-sm">lotti vini &amp; spirits</span>
            </div>
            {results?.cache_stats && (
              <p className="text-white/40 text-xs">
                Totale in DB: {results.cache_stats.total_in_db}
                {results.cache_stats.last_scraped_at && (
                  <> · Ultimo refresh: {new Date(results.cache_stats.last_scraped_at).toLocaleString("it-IT")}</>
                )}
              </p>
            )}
            <div className="flex gap-3 flex-wrap">
              <select
                value={wineCategory}
                onChange={(e) => {
                  const v = e.target.value;
                  setWineCategory(v);
                  runWineSearch({ category: v || null, seller_country: wineCountry || null });
                }}
                className="bg-black/40 border border-white/15 text-white/80 text-xs uppercase tracking-[0.18em] px-3 py-2 focus:outline-none focus:border-gold hover:border-white/30 transition-colors"
              >
                <option value="">Tutte le categorie</option>
                {(results?.categories || []).map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <select
                value={wineCountry}
                onChange={(e) => {
                  const v = e.target.value;
                  setWineCountry(v);
                  runWineSearch({ category: wineCategory || null, seller_country: v || null });
                }}
                className="bg-black/40 border border-white/15 text-white/80 text-xs uppercase tracking-[0.18em] px-3 py-2 focus:outline-none focus:border-gold hover:border-white/30 transition-colors"
              >
                <option value="">Tutti i paesi</option>
                {(results?.countries || []).map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>
          {searching && <p className="text-white/40 text-sm">Caricamento…</p>}
          {!searching && results?.items?.length > 0 && <LotGrid items={results.items} />}
          {!searching && hasSearched && (!results?.items || results.items.length === 0) && (
            <p className="text-white/40 text-sm">Nessun lotto trovato. Il job notturno popola la cache ogni notte alle 02:00 UTC.</p>
          )}
        </section>
      )}

      {isSavedMode && (
        <section
          className="relative max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24"
          data-testid="saved-searches-section"
        >
          <SavedSearches user={user} />
        </section>
      )}

      {isWatchlistMode && (
        <section
          className="relative max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24"
          data-testid="watchlist-section"
        >
          <WatchlistView user={user} onLogin={() => setAuthOpen(true)} />
        </section>
      )}

      {isSearchMode && (
        <ResultsSection
          searching={searching}
          results={results}
          hasSearched={hasSearched}
          mode={mode}
          lastQueryLabel={lastQueryLabel}
          sectionRef={resultsRef}
          loadingMore={loadingMore}
          onLoadMore={isCoglione2Mode ? loadMoreCoglione2 : loadMore}
          user={user}
          onSave={handleSaveSearch}
          lastPayload={lastPayload}
        />
      )}

      <footer className="border-t border-white/5 py-8 text-center text-xs text-white/30">
        I dati provengono da catawiki.com - Questo sito non e affiliato a Catawiki.
      </footer>

      <SettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        onStatusChange={setCookieStatus}
      />

      <AuthModal
        open={authOpen}
        onOpenChange={setAuthOpen}
        onLogin={login}
        onRegister={register}
      />

      <SaveSearchDialog
        open={saveDialogOpen}
        onOpenChange={setSaveDialogOpen}
        onConfirm={handleConfirmSave}
      />
    </div>
  );
}
